import { createFileRoute, Navigate } from "@tanstack/react-router";
import React, { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Subheading } from "src/components/base/heading";
import SINGLE_USER_CONTEXT from "src/context/internal/user";
import { Text } from "src/components/base/text";
import { DescriptionDetails, DescriptionList, DescriptionTerm } from "src/components/base/description-list";
import { Badge } from "src/components/base/badge";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "src/components/base/table";
import { Button } from "src/components/base/button";
import { ApiInternal } from "src/api/api_internal";
import DialogRenewLocalUserAccess from "src/components/dialogs/renew-local-user-access";
import { CheckIcon, ClockIcon } from "@heroicons/react/20/solid";
import { XMarkIcon } from "@heroicons/react/16/solid";

/**
 * The properties for {@link AccessUserView}
 */
export type AccessUserViewProps = {};

/**
 * The view for invitation
 */
export default function AccessUserView(props: AccessUserViewProps) {
    const [t] = useTranslation("admin-user-invitation");

    const { user, reset } = React.useContext(SINGLE_USER_CONTEXT);

    const [renewDialog, setRenewDialog] = React.useState(false);

    if (user.auth_specific.type !== "Local") {
        return <Navigate to={"/"} />;
    }
    const data = user.auth_specific;

    /**
     * Checks if the access is expired
     *
     * @returns true if the access is expired, false otherwise
     */
    const isExpired = () => !(data.has_access || data.is_access_planned);

    return (
        <div className={"flex flex-col gap-6"}>
            <Subheading>{t("heading.current-access")}</Subheading>

            <DescriptionList>
                <DescriptionTerm>{t("label.access-status")}</DescriptionTerm>
                <DescriptionDetails>
                    <Badge color={data.has_access ? "green" : data.is_access_planned ? "blue" : "zinc"}>
                        {data.has_access ? (
                            <>
                                <CheckIcon className={"size-4"} />
                                {t("label.access-valid")}
                            </>
                        ) : data.is_access_planned ? (
                            <>
                                <ClockIcon className={"size-4"} />
                                {t("label.access-planned")}
                            </>
                        ) : (
                            <>
                                <XMarkIcon className={"size-4"} />
                                {t("label.access-expired")}
                            </>
                        )}
                    </Badge>
                </DescriptionDetails>
                {isExpired() ? (
                    data.access_expires_at &&
                    new Date(data.access_start) < new Date(data.access_expires_at) && (
                        <>
                            <DescriptionTerm>{t("label.last-access-period")}</DescriptionTerm>
                            <DescriptionDetails>
                                <Text>
                                    {new Date(data.access_start).toLocaleString() +
                                        " - " +
                                        new Date(data.access_expires_at).toLocaleString()}
                                </Text>
                            </DescriptionDetails>
                        </>
                    )
                ) : (
                    <>
                        <DescriptionTerm>{t("label.access-started")}</DescriptionTerm>
                        <DescriptionDetails>
                            <Text>{new Date(data.access_start).toLocaleString()}</Text>
                        </DescriptionDetails>
                        <DescriptionTerm>{t("label.access-will-expire")}</DescriptionTerm>
                        <DescriptionDetails>
                            <Text>
                                {data.access_expires_at ? new Date(data.access_expires_at).toLocaleString() : "-"}
                            </Text>
                        </DescriptionDetails>
                    </>
                )}
            </DescriptionList>

            <div className={"flex gap-4"}>
                <Button
                    disabled={isExpired()}
                    onClick={async () => {
                        await ApiInternal.users.revokeAccess(user.uuid);
                        reset();
                    }}
                >
                    {t("button.revoke-access")}
                </Button>
                <Button disabled={!isExpired()} onClick={() => setRenewDialog(true)}>
                    {t("button.renew-access")}
                </Button>
            </div>

            <div className={"mt-4"}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableHeader>{t("label.access-created-at")}</TableHeader>
                            <TableHeader>{t("label.access-expires-at")}</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.prev_accesses.map((access) => (
                            <TableRow key={access.start}>
                                <TableHeader>{new Date(access.start).toLocaleString("de-de")}</TableHeader>
                                <TableHeader>{new Date(access.end).toLocaleString("de-de")}</TableHeader>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <Suspense>
                <DialogRenewLocalUserAccess
                    user_uuid={user.uuid}
                    isOpen={renewDialog}
                    onSubmit={() => {
                        setRenewDialog(false);
                        reset();
                    }}
                    onClose={() => setRenewDialog(false)}
                />
            </Suspense>
        </div>
    );
}

export const Route = createFileRoute("/_admin/a/users/$userId/_user/access-history")({
    component: AccessUserView,
});
